import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle} from "react";
import {
  Modal,
  Button,
  Space,
  Descriptions,
} from "antd";
import { HomeOutlined, } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import "./index.scss";
import commonFn from "@/utils/commonFn";
import { CustomTable, } from "@/hocComponents";

const PropDetailModal = forwardRef(({detailUrl, tableUrl,}, ref) => {
  const [propId, setPropId] = useState(undefined);
  const [modalName, setModalName] = useState('');
  const [isModal, setIsModal] = useState(true);
  const [modalFlag, setModalFlag] = useState(false);
  const [detail, setDetail] = useState({});

  const initTableHead = {
    propId: undefined,
    propUnit: undefined,
    street: undefined,
    unitTypeName: undefined,
    bedrooms: undefined,
    sqFeet: undefined,
    tenantStatus: undefined,
    moveInDate: undefined,
    moveOutDate: undefined,
  };
  const [tableHead, setTableHead] = useState(initTableHead);
  const [tableList, setTableList] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const tableFilterFn = (event, key, type = "Input") => {
    commonFn.tableFilterFn({
      event,
      key,
      type,
      setTableHead,
      getTableListFn,
    });
  };

  const initColumns = [
    {
      title: '#',
      dataIndex: '#',
    },
    {
      title: "Prop-Unit",
      dataIndex: "propUnit",
      titleType: "",
      onChange: (e) => tableFilterFn(e, "propUnit"),
    },
    {
      title: "Unit Address",
      dataIndex: "street",
      titleType: "",
      onChange: (e) => tableFilterFn(e, "street"),
    },
    {
      title: "Unit Type",
      dataIndex: "unitTypeName",
      titleType: "",
      onChange: (e) => tableFilterFn(e, "unitTypeName"),
    },
    {
      title: "Bed/Bath",
      dataIndex: "bedrooms",
      titleType: "",
      onChange: (e) => tableFilterFn(e, "bedrooms"),
      render: (text, record) => {
        const { bedrooms, bathrooms } = record
        return `${bedrooms} / ${bathrooms}`
      },
    },
    {
      title: "S.F.",
      dataIndex: "sqFeet",
      titleType: "",
      width: 120,
      onChange: (e) => tableFilterFn(e, "sqFeet"),
    },
    {
      title: "Tenant Status",
      dataIndex: "tenantStatus",
      titleType: "",
      selectOptions: [
        {
          label: "Curren",
          value: "C",
        },
        {
          label: "Vacant",
          value: "V",
        },
      ],
      onChange: (e) => tableFilterFn(e, "status", "Select"),
    },
    {
      title: 'Move In Date',
      dataIndex: "moveInDate",
      titleType: '',
      isSorter: false,
      format: 'MM/DD/YYYY',
      onChange: (e) => tableFilterFn(e, 'moveInDate', 'DatePicker'),
      render: (text) => {
        return /9999|10000/.test(text)  ? 'N/A' : text
      },
    },
    {
      title: 'Move Out Date',
      dataIndex: "moveOutDate",
      titleType: '',
      isSorter: false,
      format: 'MM/DD/YYYY',
      onChange: (e) => tableFilterFn(e, 'moveOutDate', 'DatePicker'),
      render: (text) => {
        return /9999|10000/.test(text) ? 'N/A' : text
      },
    },
  ];

  const [columns, setColumns] = useState(initColumns);
  
  useImperativeHandle(ref, () => ({
    openModal: ({modalName, propId, isModal = true}) => {
      setModalFlag(false)
      setModalName(modalName)
      setPropId(propId)
      setIsModal(isModal)
      setTableHead({
        ...initTableHead,
        propId
      })
      setModalFlag(true)
    }
  }));

  const afterOpenChangeFn = (flag) => {
    if(flag){
      getDetailFn()
      getTableListFn()
    }else{
      setDetail({})
      setTableHead({})
      setTableList([])
      setTableLoading(false);
    }
  }

  useEffect(() => {
    if(!isModal){
      getDetailFn()
      getTableListFn()
    }
    return () => {
      setDetail({})
      setTableHead({})
      setTableList([])
      setTableLoading(false);
    }
  }, [propId])

  const getDetailFn = async() => {
    const response = await http.get(detailUrl || api.businessLicensePropDetails, {params: {propId}});
    if(response?.code === '1'){
      setDetail({
        ...response?.data,
        startDate: /9999|10000/.test(response?.data?.startDate) ? 'N/A' : response?.data?.startDate
      })
    } else {
      commonFn.modalFailedFn(response)
    }
  }

  const detailDocFn = () => {
    const list = [
      { label: 'Management Company', value: 'companyName'},
      { label: 'County', value: 'county'},
      { label: 'Entity', value: 'trust'},
      { label: 'Prop Type', value: 'propType'},
      { label: 'Prop#', value: 'prop'},
      { label: 'Units', value: 'units'},
      { label: 'Address,City,State', value: 'street'},
      { label: 'Purchase Date', value: 'startDate'},
      { label: 'Zip', value: 'zip'},
      { label: '', value: ''},
     ]
    const items =  list.map(item => {
      return {
        label: item.label,
        children: detail?.[item.value] || ' ',
      }
    })
    return (
      <Descriptions 
        title={null}
        className="businessLicenseDescriptions"
        size="middle "
        column={2}
        items={items} 
      />
    )
  }

  const getTableListFn = async() => {
    setTableLoading(true);
    const response = await http.get(tableUrl || api.businessLicensePropList, {params: {...tableHead }});
    if (response?.code !== "1") {
      commonFn.modalFailedFn(response);
      setTableList([])
      setTableLoading(false);
      return;
    }
    setTableList(response?.data || []);
    setTableLoading(false);
  }

  const baseDocFn = () => {
    const baseDoc = (
      <>
        {detailDocFn()}
        <CustomTable
          tableLoading={tableLoading}
          rowKey="vendorPaymentId"
          columns={columns}
          tableList={tableList}
          pagination={false}
          handleTableChange={() => {}}
          tableScrollYAll="calc(80vh - 200px - 150px)"
        />
      </>
    )
    
    const modalDoc = (
      <Modal
        title={
          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#3c8dbc" }}>
            <HomeOutlined style={{ marginRight: "8px" }} />
            {modalName}
            <div
              style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px" }}
            ></div>
          </div>
        }
        centered
        open={modalFlag}
        onCancel={() => setModalFlag(false)}
        footer={null}
        maskClosable={false}
        afterOpenChange={afterOpenChangeFn}
        width="80%"
      >
        {baseDoc}
        <div style={{textAlign:'center', marginTop: '30px'}}>
          <Space align="center">
            <Button key="back" onClick={() => setModalFlag(false)}>
              Cancel
            </Button>
          </Space>
        </div>
      </Modal>
    )
    return isModal ? modalDoc : baseDoc
  }

  return (
    baseDocFn()
  );
});

export default PropDetailModal;